import React from "react";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import ContentLoader from "react-content-loader";
import { Navbar } from "./components/Navbar";
import { Layout } from "./components/Layout";
import { ReturningPatientInsuranceReviewCard } from "./components/ReturningPatientInsuranceReviewCard";
import { DocumentType } from "./types";
import { Introduction } from "./components/Introduction";
import { useProgressGuard } from "./hooks/useProgressGuard";
import { Fieldset } from "./components/Fieldset";
import { DocumentAcknowledgment } from "./components/DocumentAcknowledgment";
import styles from "./ReviewYourInsurance.module.scss";
import { InsuranceNotFound } from "./components/InsuranceNotFound";
import { Button } from "components/Button";
import { useCurrentPatientInsurance } from "features/checkIn/hooks/useCurrentPatientInsurance";
import { Insurance } from "types/insurance";
import { useCurrentPatient } from "features/patientDashboard/hooks/useCurrentPatient";
import { useSnackbar } from "features/patientDashboard/hooks/useSnackbar";
import { verifyInsuranceRequest } from "api/verifyInsuranceRequest";
import * as paths from "features/routing/paths";
import { verifyPendingInsuranceRequest } from "api/verifyPendingInsuranceRequest";
import {
  setInsuranceData,
  setCurrentInsurance,
  setIsEditForm,
  setShowAddFooter,
} from "features/insuranceDataSlice";
import { useCurrentAppointment } from "features/patientDashboard/hooks/useCurrentAppointment";
import {
  insuranceNewVerification
} from "features/checkIn/helpers";
import {
  useCheckInPageTracking,
  useCheckInNavigationTracking,
  useCheckInTracking,
} from "features/checkIn/hooks/useCheckInTracking";

export const ReviewYourInsurance: React.FC = () => {
  //useProgressGuard({ field: "insurance" });
  useCheckInPageTracking({ step: 5, page: "existing_insurance" });
  const hasSubmittedRef = React.useRef(false);
  useCheckInNavigationTracking(
    { step: 5, page: "existing_insurance" },
    hasSubmittedRef
  );
  const { trackCheckInEvent } = useCheckInTracking({
    step: 5,
    page: "existing_insurance",
  });
  const event = (action: string) => `step_5_dci_${action}`;
  const { currentPatientInsurance } = useCurrentPatientInsurance();
  const { currentAppointment, fetchCurrentAppointment } =
    useCurrentAppointment();
  const checkin = currentAppointment?.checkin;

  const history = useHistory();
  const dispatch = useDispatch();

  React.useEffect(() => {
    fetchCurrentAppointment();
  }, [fetchCurrentAppointment]);

  React.useEffect(() => {
    dispatch(setInsuranceData([{}, {}]));
    dispatch(setCurrentInsurance(0));
    dispatch(setIsEditForm(false));
    dispatch(setShowAddFooter(false));
  }, [dispatch]);

  const { handleUnauthorized } = useCurrentPatient();
  const reviewYourInsuranceFormMethods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = reviewYourInsuranceFormMethods;
  const { displayMessage } = useSnackbar();

  const onSubmit = async () => {
    await handleUnauthorized(async () => {
      if (currentPatientInsurance && currentPatientInsurance[0] && !insuranceNewVerification(checkin)) {
        try {
          await verifyInsuranceRequest(currentPatientInsurance[0]);
          await fetchCurrentAppointment();
          history.push(paths.checkInHome());
        } catch (e) {
          if (e.code === "422") {
            trackCheckInEvent("already_submitted");
            displayMessage({ icon: "checkmark", text: "Already submitted." });
            await fetchCurrentAppointment();
            history.push(paths.checkInHome());
            return;
          }
          hasSubmittedRef.current = false;
          trackCheckInEvent("api_failure", {
            api: "verify_insurance",
          });
          throw e;
        }
      } else if (insuranceNewVerification(checkin)) {
        try {
          await verifyPendingInsuranceRequest();
          await fetchCurrentAppointment();
          history.push(paths.checkInHome());
        } catch (e) {
          if (e.code === "422") {
            trackCheckInEvent("already_submitted");
            // displayMessage({ icon: "checkmark", text: "Already submitted." });
            await fetchCurrentAppointment();
            history.push(paths.checkInHome());
            return;
          }
          hasSubmittedRef.current = false;
          trackCheckInEvent("api_failure", {
            api: "verify_pending_insurance",
          });
          throw e;
        }
      } else if (
        currentPatientInsurance &&
        currentPatientInsurance.length === 0 
      ) {
        try {
          await verifyPendingInsuranceRequest();
          await fetchCurrentAppointment();
          history.push(paths.checkInHome());
        } catch (e) {
          if (e.code === "422") {
            trackCheckInEvent("already_submitted");
            displayMessage({ icon: "checkmark", text: "Already submitted." });
            await fetchCurrentAppointment();
            history.push(paths.checkInHome());
            return;
          }
          hasSubmittedRef.current = false;
          trackCheckInEvent("api_failure", {
            api: "verify_pending_insurance",
          });
          throw e;
        }
      }
    });
  };

  const isLoading = !currentPatientInsurance || !checkin;

  return (
    <FormProvider {...reviewYourInsuranceFormMethods}>
      <Layout
        navbar={<Navbar title={"Insurance"} />}
        primaryCta={
          <Button
            onClick={handleSubmit(async () => {
              hasSubmittedRef.current = true;
              trackCheckInEvent(event("save_continue"));
              await onSubmit();
            }, () => {
              trackCheckInEvent("validation_error");
            })}
            renderAsDisabled={!isValid || isLoading}
            loading={isSubmitting}
            disabled={isSubmitting || isLoading}
            text="Save & Continue"
          />
        }
        emptyHeight={true}
      >
        <Introduction header="Review your coverage">
          We use this information to verify your coverage, please ensure details
          are accurate.
        </Introduction>
        {currentPatientInsurance ? (
          currentPatientInsurance.length > 0 ? (
            currentPatientInsurance.map(
              (insurance: Insurance, i: React.Key | null | undefined) => {
                return (
                  <ReturningPatientInsuranceReviewCard
                    key={i}
                    insurance={insurance}
                  />
                );
              }
            )
          ) : (
            <InsuranceNotFound />
          )
        ) : (
          <div className={styles.container}>
            <div className={styles.card}>
              <ContentLoader viewBox="0 0 760 254" style={{ width: "100%" }}>
                <rect x="6" y="1" rx="3" ry="3" width="216" height="15" />
                <rect x="6" y="66" rx="3" ry="3" width="137" height="16" />
                <rect x="6" y="42" rx="3" ry="3" width="137" height="16" />
                <rect x="6" y="111" rx="3" ry="3" width="137" height="16" />
                <rect x="6" y="135" rx="3" ry="3" width="137" height="16" />
                <rect x="6" y="176" rx="3" ry="3" width="137" height="16" />
                <rect x="6" y="200" rx="3" ry="3" width="137" height="16" />
                <rect x="600" y="42" rx="3" ry="3" width="137" height="16" />
              </ContentLoader>
            </div>
          </div>
        )}
        <hr className={styles.separator} />
        <div className={styles.agreement}>
          <Fieldset
            legend="Insurance Assignment Policy & Agreement"
            legendClassName={styles.alwaysVisible}
          >
            <DocumentAcknowledgment
              documentType={DocumentType.InsuranceAssignmentPolicy}
              trackingContext={{ step: 5, page: "existing_insurance" }}
            />
          </Fieldset>
        </div>
      </Layout>
    </FormProvider>
  );
};
